import { mascaraPlaca } from '@/nucleo/formato/documentos';
import { Badge } from '@/ui/base/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';

import type { VeiculoDoCredenciado } from './contrato';

/**
 * A frota do credenciado, resumida numa celula.
 *
 * POR QUE ESTA COLUNA EXISTE NA LISTA DE CREDENCIADOS. Quem atende o telefone recebe a
 * licenca ou a placa, nunca o nome da empresa — "o CRM 12345 esta com problema". Sem
 * isto, a unica forma de chegar ao credenciado a partir do veiculo seria abrir a tela
 * de veiculos, buscar, e voltar.
 *
 * O CRM VEM EM DESTAQUE E A PLACA ABAIXO, e a hierarquia e essa porque o CRM e o
 * documento do TEG: e ele que aparece no termo, na ordem de servico e na medicao. A
 * placa e do Detran, muda quando o veiculo e substituido e serve para conferir na rua.
 *
 * VEICULO SEM CRM GANHA AVISO EM VEZ DE CELULA VAZIA. Placa sem licenca municipal
 * significa veiculo que ainda nao pode rodar no programa — e uma pendencia, nao um
 * dado faltando, e a diferenca decide se alguem vai atras dela.
 *
 * O corte em "+N" existe porque cooperativa tem dezenas de veiculos: listar todos
 * esticaria a linha e empurraria a tabela para rolagem horizontal, proibida a partir de
 * 375 px (CLAUDE.md, "Regras de interface", item 4). O tooltip guarda o resto.
 */

interface LicencaPlacaCellProps {
  veiculos: readonly VeiculoDoCredenciado[] | null | undefined;
  /** Quantos aparecem antes de o resto virar "+N". */
  maxVisiveis?: number;
  /** Ignora veiculo que nao esta ativo — o que a listagem de credenciados quer. */
  somenteAtivos?: boolean;
}

export function LicencaPlacaCell({
  veiculos,
  maxVisiveis = 2,
  somenteAtivos = false,
}: LicencaPlacaCellProps) {
  const lista = (veiculos ?? []).filter((veiculo) => {
    if (!veiculo) {
      return false;
    }
    if (somenteAtivos && veiculo.status && veiculo.status !== 'ATIVO') {
      return false;
    }
    return Boolean(veiculo.placa || veiculo.crm);
  });

  if (lista.length === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }

  const visiveis = lista.slice(0, maxVisiveis);
  const restantes = lista.slice(maxVisiveis);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {visiveis.map((veiculo, indice) => (
        <div
          key={veiculo.crm ?? veiculo.placa ?? indice}
          className="flex flex-col text-xs leading-tight"
        >
          {veiculo.crm ? (
            <>
              <span className="font-mono font-semibold tabular-nums">{veiculo.crm}</span>
              {veiculo.placa && (
                <span className="text-muted-foreground font-mono text-[10px]">
                  {mascaraPlaca(veiculo.placa)}
                </span>
              )}
            </>
          ) : (
            <>
              <span className="font-mono font-semibold">{mascaraPlaca(veiculo.placa)}</span>
              <span className="text-warning text-[10px]">licença pendente</span>
            </>
          )}
        </div>
      ))}

      {restantes.length > 0 && (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="cursor-help text-xs">
                +{restantes.length}
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="flex flex-col gap-1 text-xs">
                {restantes.map((veiculo, indice) => (
                  <div key={veiculo.crm ?? veiculo.placa ?? indice} className="font-mono">
                    {veiculo.crm ?? '—'}
                    {veiculo.placa ? ` · ${mascaraPlaca(veiculo.placa)}` : ''}
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}
